import { FaHandHoldingHeart, FaUsers, FaBalanceScale, FaHandshake, FaLockOpen, FaMoneyCheckAlt, FaMosque, FaRoute, FaLink } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const MasarifEZakat = () => {
  const { t } = useTranslation("masarif");
  const translation = t('masarif', { returnObjects: true });

  const categories = [
    { 
      icon: FaHandHoldingHeart, 
      title: translation.categories[0].title, 
      color: "text-red-500", 
      des: translation.categories[0].description 
    },
    { 
      icon: FaUsers, 
      title: translation.categories[1].title, 
      color: "text-orange-500", 
      des: translation.categories[1].description 
    },
    { 
      icon: FaBalanceScale, 
      title: translation.categories[2].title, 
      color: "text-amber-500", 
      des: translation.categories[2].description 
    },
    { 
      icon: FaHandshake, 
      title: translation.categories[3].title, 
      color: "text-emerald-500", 
      des: translation.categories[3].description 
    },
    { 
      icon: FaLockOpen, 
      title: translation.categories[4].title, 
      color: "text-teal-500", 
      des: translation.categories[4].description 
    },
    { 
      icon: FaMoneyCheckAlt, 
      title: translation.categories[5].title, 
      color: "text-blue-500", 
      des: translation.categories[5].description 
    },
    { 
      icon: FaMosque, 
      title: translation.categories[6].title, 
      color: "text-indigo-500", 
      des: translation.categories[6].description 
    },
    { 
      icon: FaRoute, 
      title: translation.categories[7].title, 
      color: "text-purple-500", 
      des: translation.categories[7].description 
    },
  ];

  return (
    <>
      <Helmet>
        <title>Who is eligible for Zakat? | Masarif-e-Zakat Guide</title>
        
        <meta name="description" content="Learn about the 8 categories of people eligible to receive Zakat in Islam. Understand the divinely ordained recipients of Zakat and how your charity helps." />
        <meta name="keywords" content="Zakat, Masarif e Zakat, Islamic Charity, Zakat Recipients, Fisabilillah, Gharimeen, Ibn Sabeel, Poor Needy Muslims, who is eligible for zakat, eligible for zakat, who is eligible to pay zakat, who pays zakat, donate zakat pakistan, zakat donation portal, online zakat, zakat pakistan, zakat fund, zakat donations, zakat in islam, muslim zakat, zakat fund in pakistan, Zakat eligibility, who can receive Zakat, Zakat recipients, Islamic charity, Zakat for poor people, Zakat for needy, Zakat administrators, Zakat for new Muslims, Zakat for slaves, Zakat for debt relief, Zakat for Allah's cause, Zakat for travelers, giving Zakat to relatives, Zakat in Islam, purifying wealth, Sadaqah, Islamic giving, charity for poor families, Zakat rules, Zakat categories, فقراء کے لیے زکوٰۃ, مساکین کے لیے زکوٰۃ, زکوٰۃ کے مستحق, اسلامی خیرات, زکوٰۃ کے اصول, قرض کی ادائیگی کے لیے زکوٰۃ, رشتہ داروں کو زکوٰۃ دینا, اللہ کی راہ میں زکوٰۃ, مسافروں کے لیے زکوٰۃ, صدقہ, زکوٰۃ کی شرائط, who is eligible for Zakat in Islam, how to give Zakat, Zakat and charity, Islamic obligations Zakat, Zakat for community support, زکوٰۃ اور خیرات, زکوٰۃ سے دولت کی پاکیزگی" />
        <meta name="robots" content="index, follow" />

        <meta property="og:title" content="Who Can Receive Zakat?" />
        <meta property="og:description" content="Discover the divinely ordained categories eligible for Zakat. Understand the importance of each recipient." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://addazakat.com/who-to-pay-zakat" />
        <link rel="canonical" href="https://addazakat.com/who-to-pay-zakat" />
      </Helmet>

      <div className="flex items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 md:p-12 lg:p-16 mt-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-500 dark:from-emerald-400 dark:to-teal-300 mb-6">
              {translation.heading}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {translation.subheading}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {categories.map(({ icon: Icon, title, color, des }, index) => (
              <div key={index} className="group bg-white dark:bg-gray-700 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start mb-4">
                  <div className={`p-3 rounded-lg ${color} bg-opacity-20 mr-4`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                    {title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 pl-16">{des}</p>
              </div>
            ))}
          </div>

          <div className="bg-emerald-50 dark:bg-emerald-900 rounded-2xl p-8 md:p-12 mb-16">
            <div className="flex items-center mb-6">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-800 mr-4">
                <FaLink className="w-8 h-8 text-emerald-600 dark:text-emerald-300" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">
                {translation.relatives_section.title}
              </h2>
            </div>
            
            <blockquote className="border-l-4 border-emerald-500 pl-6 mb-8">
              <p className="text-lg italic text-gray-700 dark:text-gray-300 mb-4">
                {translation.relatives_section.hadith.text}
                <span className="block mt-2 text-emerald-600 dark:text-emerald-300 font-medium">
                  - {translation.relatives_section.hadith.source}
                </span>
              </p>
            </blockquote>

            <div className="grid gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl">
                <ul className="space-y-4">
                  {translation.relatives_section.points.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      {item}
                    </li>
                  ))}
                </ul>

                <p className="text-sm mt-5 font-bold text-gray-700 dark:text-gray-300">
                  {translation.relatives_section.footer}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center py-8">
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 italic font-medium">
              {translation.conclusion}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MasarifEZakat;